import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { Trash, Shield } from "@phosphor-icons/react";

export function AdminUsers() {
  const [users, setUsers] = useState([]);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data.users);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  useEffect(() => { load(); }, []);

  const toggleRole = async (u) => {
    const newRole = u.role === "admin" ? "user" : "admin";
    try {
      await api.patch(`/admin/users/${u.id}?role=${newRole}`);
      toast.success(`Role updated: ${u.email} → ${newRole}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const del = async (u) => {
    if (!window.confirm(`Delete ${u.email}?`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="p-8">
      <header className="mb-6 pb-6 border-b border-[#262626]">
        <div className="text-[10px] uppercase tracking-widest text-[#71717A] mb-2">Admin</div>
        <h1 className="font-display text-4xl font-black tracking-tight">Users</h1>
      </header>
      <div className="border border-[#262626] bg-[#0F0F0F]" data-testid="admin-users-table">
        <Table>
          <TableHeader>
            <TableRow className="border-[#262626] hover:bg-transparent">
              <TableHead className="text-[#71717A] uppercase text-[10px] tracking-widest">Email</TableHead>
              <TableHead className="text-[#71717A] uppercase text-[10px] tracking-widest">Name</TableHead>
              <TableHead className="text-[#71717A] uppercase text-[10px] tracking-widest">Role</TableHead>
              <TableHead className="text-[#71717A] uppercase text-[10px] tracking-widest">Created</TableHead>
              <TableHead className="text-[#71717A] uppercase text-[10px] tracking-widest text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map((u) => (
              <TableRow key={u.id} className="border-[#262626] hover:bg-[#141414]">
                <TableCell className="font-mono text-sm">{u.email}</TableCell>
                <TableCell>{u.name}</TableCell>
                <TableCell><Badge className="rounded-sm font-mono text-[10px] uppercase" variant={u.role === "admin" ? "default" : "outline"}>{u.role}</Badge></TableCell>
                <TableCell className="text-xs font-mono text-[#71717A]">{u.created_at ? new Date(u.created_at).toLocaleDateString() : ""}</TableCell>
                <TableCell className="text-right space-x-2">
                  <Button size="sm" variant="outline" className="rounded-sm border-[#262626] bg-transparent hover:bg-[#141414]" onClick={() => toggleRole(u)} data-testid={`toggle-role-${u.id}`}>
                    <Shield size={12} className="mr-1" /> {u.role === "admin" ? "Demote" : "Promote"}
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-sm border-[#262626] bg-transparent hover:bg-[#EF4444] hover:text-white" onClick={() => del(u)} data-testid={`delete-user-${u.id}`}>
                    <Trash size={12} />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

export function AdminActivity() {
  const [logs, setLogs] = useState([]);
  useEffect(() => {
    api.get("/admin/activity").then((r) => setLogs(r.data.logs)).catch(() => {});
  }, []);
  return (
    <div className="p-8">
      <header className="mb-6 pb-6 border-b border-[#262626]">
        <div className="text-[10px] uppercase tracking-widest text-[#71717A] mb-2">Admin</div>
        <h1 className="font-display text-4xl font-black tracking-tight">Activity logs</h1>
      </header>
      <div className="border border-[#262626] bg-[#0F0F0F]" data-testid="activity-table">
        <Table>
          <TableHeader>
            <TableRow className="border-[#262626] hover:bg-transparent">
              <TableHead className="text-[#71717A] uppercase text-[10px] tracking-widest">When</TableHead>
              <TableHead className="text-[#71717A] uppercase text-[10px] tracking-widest">User</TableHead>
              <TableHead className="text-[#71717A] uppercase text-[10px] tracking-widest">Action</TableHead>
              <TableHead className="text-[#71717A] uppercase text-[10px] tracking-widest">Meta</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {logs.length === 0 && <TableRow className="border-[#262626]"><TableCell colSpan={4} className="text-center py-12 text-[#71717A] dashed">No activity logged.</TableCell></TableRow>}
            {logs.map((l) => (
              <TableRow key={l.id} className="border-[#262626] hover:bg-[#141414]">
                <TableCell className="text-xs font-mono text-[#A1A1AA]">{new Date(l.created_at).toLocaleString()}</TableCell>
                <TableCell className="font-mono text-xs">{l.user_id?.slice(-8) || "-"}</TableCell>
                <TableCell className="font-mono">{l.action}</TableCell>
                <TableCell className="text-xs font-mono text-[#71717A]">{JSON.stringify(l.meta)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
